import { useMutation, useQueryClient, UseMutationOptions } from '@tanstack/react-query';
import { deleteUser, type User } from '@/api/entities/users';
import { ExtractFnReturnType } from '@/lib/react-query';
import { useCognitoAuth } from '@/hooks/useCognitoAuth';
import { GraphQLRequestError } from '@/api/graphql';

type QueryFnType = typeof deleteUser;

type UseDeleteUserOptions = {
  config?: Omit<
    UseMutationOptions<ExtractFnReturnType<QueryFnType>, GraphQLRequestError, User>,
    'mutationFn'
  >;
};

export const useDeleteUser = ({ config }: UseDeleteUserOptions = {}) => {
  const queryClient = useQueryClient();
  const { getAccessToken } = useCognitoAuth();

  return useMutation<ExtractFnReturnType<QueryFnType>, GraphQLRequestError, User>({
    ...config,
    mutationFn: async (user: User) => {
      return deleteUser(user.id, getAccessToken);
    },
    onSuccess: (data, variables, onMutateResult, context) => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      queryClient.invalidateQueries({ queryKey: ['users', variables.id] });
      config?.onSuccess?.(data, variables, onMutateResult, context);
    },
  });
};
